import { useState } from 'react'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { toast } from 'sonner'
import { HugeiconsIcon } from '@hugeicons/react'
import { Delete01Icon, ArrowRight01Icon } from '@hugeicons/core-free-icons'

import {
  useAdminOrganizationDetail,
  useAdminDeleteOrganization,
  useAdminUpdateOrganization,
  useAdminForceTransferOwnership,
  useAdminResetUpcomingPeriods,
  useAdminRunPopulatePeriodsNow,
  useAdminPeriodConfig,
  useAdminUpdatePeriodConfig,
} from '@/features/admin/services/admin.convex'
import { PeriodConfigFormInner } from '@/features/organization/components'
import { useConfirmDialog } from '@/contexts/confirm-dialog-context'
import { useFormDialog } from '@/contexts/form-dialog-context'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import type { AnyFieldApi } from '@tanstack/react-form'

export const Route = createFileRoute(
  '/(dashboard)/admin/organizations/$organizationId/settings/',
)({
  component: RouteComponent,
  validateSearch: (search: Record<string, unknown>) => ({
    name: (search.name as string) || undefined,
  }),
  beforeLoad: ({ params, search }) => ({
    breadcrumbs: [
      { title: 'Admin' },
      { title: 'Organizations', href: '/admin/organizations' },
      { title: search.name || params.organizationId },
      { title: 'Settings' },
    ],
  }),
})

function RouteComponent() {
  const { organizationId } = Route.useParams()
  const navigate = useNavigate()
  const { showConfirmDialog } = useConfirmDialog()
  const { showFormDialog } = useFormDialog()

  const { data: org } = useAdminOrganizationDetail(organizationId)
  const deleteOrg = useAdminDeleteOrganization()
  const updateOrg = useAdminUpdateOrganization()
  const forceTransfer = useAdminForceTransferOwnership()
  const resetPeriods = useAdminResetUpcomingPeriods(organizationId)
  const runPopulateNow = useAdminRunPopulatePeriodsNow(organizationId)
  const { data: periodConfig } = useAdminPeriodConfig(organizationId)
  const adminUpdatePeriodConfig = useAdminUpdatePeriodConfig(organizationId)

  const [nameInput, setNameInput] = useState('')
  const [descInput, setDescInput] = useState('')
  const [saving, setSaving] = useState(false)

  if (!org) return null

  const orgName = org.name
  const orgDesc = org.description ?? ''

  const handleSave = async () => {
    const name = nameInput || orgName
    if (!name.trim()) return
    setSaving(true)
    try {
      await updateOrg({
        organizationId: organizationId as never,
        name: name.trim(),
        description: descInput.trim() || undefined,
      })
      toast.success('Organization updated')
    } catch {
      toast.error('Failed to update organization')
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = () => {
    showConfirmDialog({
      title: 'Delete Organization',
      description: `This will permanently delete "${org.name}" and remove all its members. This cannot be undone.`,
      confirmText: 'Delete',
      variant: 'destructive',
      requireInput: { match: org.name },
      onConfirm: async () => {
        try {
          await deleteOrg({ organizationId: organizationId as never })
          toast.success(`Organization "${org.name}" deleted`)
          navigate({ to: '/admin/organizations' })
        } catch {
          toast.error('Failed to delete organization')
        }
      },
    })
  }

  const handleForceTransfer = () => {
    const members = (org.members ?? []).filter(
      (m): m is NonNullable<typeof m> => m !== null,
    )

    showFormDialog<{ newOwnerId: string }, void>({
      title: 'Force Transfer Ownership',
      description: `Override the current owner of "${org.name}". The selected member will be set as the new owner and granted admin access.`,
      submitText: 'Transfer ownership',
      submitVariant: 'warning',
      showSuccessState: false,
      formOptions: { defaultValues: { newOwnerId: '' } },
      isSubmitDisabled: (form) => !form.state.values.newOwnerId,
      children: (form) => (
        <form.Field name="newOwnerId">
          {(field: AnyFieldApi) => (
            <div className="space-y-2">
              <Label htmlFor="forceNewOwner">New owner</Label>
              <Select
                value={field.state.value}
                onValueChange={(v) => field.handleChange(v)}
              >
                <SelectTrigger id="forceNewOwner" className="w-full">
                  <SelectValue placeholder="Select a member…" />
                </SelectTrigger>
                <SelectContent>
                  {members.map((m) => (
                    <SelectItem key={m.id} value={m.id}>
                      <span className="font-medium">{m.name || m.email}</span>
                      {m.name ? (
                        <span className="text-muted-foreground ml-2 text-xs">
                          {m.email}
                        </span>
                      ) : null}
                      {org.createdBy === m.id ? (
                        <span className="text-muted-foreground ml-2 text-xs">
                          (current owner)
                        </span>
                      ) : null}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </form.Field>
      ),
      onSubmit: async (data) => {
        await forceTransfer({
          organizationId: organizationId as never,
          newOwnerId: data.newOwnerId as never,
        })
        toast.success('Ownership transferred successfully')
      },
    })
  }

  return (
    <div className="space-y-6">
      {/* Name & Description */}
      <div className="bg-muted/30 rounded-lg p-6 space-y-4">
        <div>
          <h3 className="text-base font-semibold text-foreground">
            Organization Details
          </h3>
          <p className="text-sm text-muted-foreground mt-0.5">
            Update the name and description for this organization.
          </p>
        </div>
        <div className="flex items-end gap-3">
          <div className="space-y-1.5 flex-1">
            <Label htmlFor="org-name-edit">Name</Label>
            <Input
              className="bg-background"
              id="org-name-edit"
              defaultValue={orgName}
              onChange={(e) => setNameInput(e.target.value)}
              placeholder="Organization name"
            />
          </div>
          <div className="space-y-1.5 flex-1">
            <Label htmlFor="org-desc-edit">Description</Label>
            <Input
              className="bg-background"
              id="org-desc-edit"
              defaultValue={orgDesc}
              onChange={(e) => setDescInput(e.target.value)}
              placeholder="Short description…"
            />
          </div>
          <Button onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : 'Save Changes'}
          </Button>
        </div>
      </div>

      {/* Period Configuration */}
      {periodConfig ? (
        <PeriodConfigFormInner
          config={periodConfig}
          updateFn={adminUpdatePeriodConfig.mutateAsync}
        />
      ) : null}

      {/* Period Actions */}
      <div className="bg-muted/30 rounded-lg p-6 space-y-4">
        <div>
          <h3 className="text-base font-semibold text-foreground">
            Period actions
          </h3>
          <p className="text-sm text-muted-foreground mt-0.5">
            Use these after changing the period configuration above. The system
            pre-schedules future periods based on the saved settings — these
            actions let you clear and rebuild them immediately without waiting
            for the daily scheduler.
          </p>
        </div>
        <div className="flex flex-wrap gap-3">
          <div className="flex-1 min-w-64 p-4 rounded-md border bg-background space-y-2">
            <p className="text-sm font-medium">Reset upcoming periods</p>
            <p className="text-xs text-muted-foreground">
              Throws away all future pre-scheduled periods that haven't sent
              emails yet. Run this after saving a new cycle length or start date
              so the stale periods don't override your changes. Periods that
              already sent emails are never touched.
            </p>
            <Button
              variant="outline"
              size="sm"
              disabled={resetPeriods.isPending}
              onClick={async () => {
                showConfirmDialog({
                  title: 'Reset upcoming periods',
                  description: `Delete all future unsent periods for "${org.name}"? Active and past periods are preserved.`,
                  confirmText: 'Reset',
                  cancelText: 'Cancel',
                  variant: 'destructive',
                  onConfirm: async () => {
                    const result = await resetPeriods.mutateAsync()
                    toast.success(
                      result.deletedCount === 0
                        ? 'No upcoming unsent periods to clear'
                        : `Cleared ${result.deletedCount} upcoming period${result.deletedCount === 1 ? '' : 's'}`,
                    )
                  },
                })
              }}
            >
              {resetPeriods.isPending ? 'Resetting…' : 'Reset upcoming periods'}
            </Button>
          </div>
          <div className="flex-1 min-w-64 p-4 rounded-md border bg-background space-y-2">
            <p className="text-sm font-medium">Generate period now</p>
            <p className="text-xs text-muted-foreground">
              Immediately rebuilds the next period using the current saved
              settings, instead of waiting for the nightly 06:00 UTC job. Run
              this after resetting periods so the org has an active period right
              away. Has no effect if periods are already up to date.
            </p>
            <Button
              variant="outline"
              size="sm"
              disabled={runPopulateNow.isPending}
              onClick={async () => {
                showConfirmDialog({
                  title: 'Generate the next period now?',
                  description: `Run period generation immediately for "${org.name}"? If a period already covers today, nothing will change.`,
                  confirmText: 'Generate period',
                  cancelText: 'Cancel',
                  onConfirm: async () => {
                    const result = await runPopulateNow.mutateAsync()
                    if (result.scheduled) {
                      toast.success(
                        'Period generation scheduled — refresh in a few seconds to see it.',
                      )
                    } else {
                      toast.info(result.reason)
                    }
                  },
                })
              }}
            >
              {runPopulateNow.isPending ? 'Scheduling…' : 'Generate period now'}
            </Button>
          </div>
        </div>
      </div>

      {/* Force Transfer Ownership */}
      <div className="p-6 rounded-lg bg-warning/10 border border-warning/30 flex items-center justify-between gap-6">
        <div>
          <h3 className="text-base font-semibold text-foreground">
            Force transfer ownership
          </h3>
          <p className="text-sm text-muted-foreground mt-1">
            Override the current owner and assign ownership to another active
            member. Use this to recover from unauthorized ownership changes.
            {org.createdBy ? (
              <span className="block mt-1">
                Current owner ID:{' '}
                <span className="font-mono text-xs">{org.createdBy}</span>
              </span>
            ) : null}
          </p>
        </div>
        <Button
          variant="warning"
          onClick={handleForceTransfer}
          className="shrink-0"
        >
          <HugeiconsIcon icon={ArrowRight01Icon} className="mr-2 h-4 w-4" />
          Transfer ownership
        </Button>
      </div>

      {/* Danger Zone */}
      <div className="p-6 rounded-lg bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-800 flex items-center justify-between gap-6">
        <div>
          <h3 className="text-base font-semibold text-foreground">
            Danger Zone
          </h3>
          <p className="text-sm text-muted-foreground mt-1">
            Permanently delete this organization and remove all its members.
            This action is irreversible.
          </p>
        </div>
        <Button
          variant="destructive"
          onClick={handleDelete}
          className="shrink-0"
        >
          <HugeiconsIcon icon={Delete01Icon} className="mr-2 h-4 w-4" />
          Delete Organization
        </Button>
      </div>
    </div>
  )
}
